import { useState } from "react";
import { Badge, Box, ListItemText, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { Check as CheckIcon, ExpandMore as ExpandMoreIcon, Preview as PreviewIcon } from "@mui/icons-material";
import MUIButton from "@/components/ui/Button";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import {
  UIComponentSize,
  ButtonStyleType,
  ConfirmLabelType,
  CancelLabelType,
  ColorType,
} from "@/types/shared/uiComponents";
import { CompletenessCheck } from "@/types";
import { buildApproveConfirm, deriveApproveStatus } from "../../utils/approveStatus";

/** DOM id of the Approve status line; the button's aria-describedby points here (#7431 AC2). */
export const APPROVE_STATUS_ID = "review-approve-status";

interface ReviewActionsProps {
  /**
   * "full" (default): Field Mapping · More · Approve — the multi-client review
   * header, where there is no rail. "more-only": just the More menu; the
   * single-client header uses this since #7432 moved Approve, its status line
   * and Field mapping into the "Before you approve" rail.
   */
  variant?: "full" | "more-only";
  onReviewMapping: () => void;
  /**
   * Re-run the document from its source file (#6920). Absent when the caller
   * cannot offer it (no edit permission, read-only partner).
   *
   * Distinct from onReviewMapping ON PURPOSE. Saving field mappings already
   * triggers a reprocess (useFieldMappingSave posts to /reprocess), so a
   * reprocess was always reachable from this screen — but only by entering the
   * Field Mapping dialog and saving, which a reviewer who wants a plain re-run
   * has no reason to do and our error copy never told them to. DOC-000001 says
   * "reprocess"; this is the control that word refers to.
   */
  onReprocess?: () => void;
  /**
   * Reviewer exclusions currently held on this screen. Used ONLY for the
   * reprocess confirm's copy, so it can state the real number instead of a
   * generic "you may lose work" (#6920 AC2).
   */
  excludedCount?: number;
  /**
   * True while a reprocess is already running for this document (!4711 review,
   * [AEM-no-inflight-guard]). Disables the control: ReprocessDocument has no
   * status precondition server-side, so a second click enqueues a second full
   * re-extraction and the grid settles on whichever worker run finishes last.
   * Approve and Reject in this component already guard this way.
   */
  isReprocessing?: boolean;
  onApprove: () => void;
  /**
   * Opens the reject flow (#7441). Since #7441 this does NOT reject — it asks
   * the owner to open RejectDocumentDialog, which collects the required reason
   * and calls useReviewApproval.handleReject. Reached from the More menu here
   * and from ClientGate's "Wrong file? Reject it…" link.
   */
  onReject: () => void;
  /**
   * Opens the document delete flow (DeleteImpactDialog). Present only when the
   * caller holds `documents:cascade-delete`; absent renders the More menu's
   * "Delete document…" item disabled with "Admins only" (#7441 AC1).
   */
  onDeleteDocument?: () => void;
  isApproving: boolean;
  isRejecting: boolean;
  isReadyForApproval: boolean;
  hasTransformation: boolean;
  isClientMissing: boolean;
  /**
   * Caller cannot approve (no documents:create) — #7431 AC2/AC4. Approve is
   * disabled with "View only. A reviewer approves this file." and the
   * destructive More items are hidden.
   */
  readOnly?: boolean;
  /** Active partner is read-only (ADR-035) — Approve says to switch partner. */
  partnerReadOnly?: boolean;
  /** Multi-client review with nothing loadable — Approve says so (#7431 AC2). */
  nothingToLoad?: boolean;
  /** Approval forecast still running — Approve waits and says so (#7431 AC2). */
  isChecking?: boolean;
  /** Paid-through date is after today; named in the status line (#7431 AC2). */
  isAsOfInFuture?: boolean;
  errorCount: number;
  /**
   * Whole-document errored records the reviewer has NOT resolved, exclusion-
   * aware. `errorCount` covers the loaded records only.
   *
   * RENAMED FROM offPageErrorCount, and the rename is the point (!4682
   * review). It used to be a subtraction — whole-document errored minus loaded
   * errored — with exclusion state in neither term. That was sound only while
   * the reviewer could not act on a record they had never been shown; #6853's
   * banner made it false. Excluding an off-page group wrote to the exclusion
   * set, nothing this prop reads moved, and Approve stayed disabled with a
   * tooltip telling the reviewer to load more records.
   *
   * "Off-page" was also the wrong frame: what gates approval is whether an
   * errored record is unresolved, not where it sits.
   */
  unresolvedErroredCount?: number;
  /**
   * False when `unresolvedErroredCount` is an upper bound rather than exact —
   * the errored read was truncated, so some errored records are neither
   * counted as excluded nor resolvable from this surface.
   *
   * Only affects COPY. The gate stays closed either way, which is the correct
   * direction: an inexact count of blocking records must not unblock.
   */
  unresolvedCountIsExact?: boolean;
  warningCount: number;
  /**
   * Blocking mapping-validation issues (duplicate target field, mutually-
   * exclusive conflicts, unknown fields). Always approval-gating. Drives the
   * Field Mapping button badge color: any blocking signal turns the badge
   * red, otherwise the badge stays warning-yellow even when the count is
   * non-zero. Reviewers shouldn't see a red badge they can dismiss with
   * "approve anyway" — that trains red-fatigue.
   */
  mappingBlockingCount: number;
  /**
   * Required-tier critical-field gaps. Drives the badge count alongside
   * blocking issues, but warning-yellow on its own (records will error on
   * approve, but the document is still mappable; this is action-needed,
   * not error-state).
   */
  mappingAdvisoryCount: number;
  /**
   * Non-blocking paid-through (as-of) warning (ADR-051) — e.g. the chosen as-of
   * predates the latest claim paid date, or the doc looks like a carrier YTD
   * report but no as-of is set, so the reported total becomes authoritative with
   * no date and newer claim lines cannot override it (ADR-051 §2, #7392).
   * When set with no errors present, approve routes through the confirmation
   * dialog (reusing ADR-045's warning-gate) and this message is appended — it
   * never disables approve.
   */
  asOfWarningMessage?: string;
  /**
   * Non-blocking Rx carve-out double-count warning (ADR-077 §8). Set when a
   * carve-out policy's document reports a combined/bare medical+Rx total that
   * may already include Rx which will also load as separately carved-out line
   * items. Joins the same confirm-before-approve gate as asOfWarningMessage —
   * never disables approve.
   */
  doubleCountWarningMessage?: string;
  /**
   * Extraction completeness verdict (#5767). A failed/unverifiable verdict
   * (e.g. "3 members missing") routes approve through the same warning-gate and
   * appends a line — warns-and-acks, never blocks. Without this, a doc that
   * likely dropped members would approve with no prompt while a stale as-of
   * warning on the same doc does prompt.
   */
  completeness?: CompletenessCheck;
}

/**
 * Copy for the reprocess confirm, shared by the More menu here and the rail's
 * "Fix the extraction" button (#7432) so the two say the same thing.
 *
 * `excludedCount` is the REVIEWER'S divergence from the auto-exclude pass, not
 * the size of the exclusion set (!4711 review, [AEM-autoexclude-attribution]).
 * The paid-through date is deliberately NOT listed: nothing resets a
 * reviewer-typed as-of on a reprocess ([AEM-asof-not-discarded]).
 *
 * "keeps the current field mappings" is not padding — a plain reprocess sends
 * no mappings, and the server then CONFIRMS the existing ones, which makes the
 * worker reuse the template instead of re-inferring
 * (!4711 review, [AEM-empty-mappings-pins-template]).
 */
export const reprocessConfirmCopy = (excludedCount: number): { pendingWork: string | null; message: string } => {
  const pendingWork =
    excludedCount > 0 ? `${excludedCount} record ${excludedCount === 1 ? "exclusion" : "exclusions"}` : null;
  const message = pendingWork
    ? `This re-runs the document from its source file and keeps the current field mappings. It discards ${pendingWork} — that work is not saved yet, so it cannot be restored afterwards.`
    : "This re-runs the document from its source file and keeps the current field mappings. Nothing you have changed on this screen will be lost. To change how columns are read, use Field Mapping instead.";
  return { pendingWork, message };
};

/**
 * Compact action buttons for the review tab — field mapping, More (reject /
 * delete), approve.
 *
 * Approval gating:
 *  - Errors present → button disabled, tooltip suggests adjusting field mapping
 *  - Only warnings  → confirmation dialog before proceeding
 *  - Clean          → approve immediately
 */
export const ReviewActions = ({
  variant = "full",
  onReviewMapping,
  onReprocess,
  excludedCount = 0,
  isReprocessing = false,
  onApprove,
  onReject,
  onDeleteDocument,
  isApproving,
  isRejecting,
  isReadyForApproval,
  hasTransformation,
  isClientMissing,
  readOnly = false,
  partnerReadOnly = false,
  nothingToLoad = false,
  isChecking = false,
  isAsOfInFuture = false,
  errorCount,
  unresolvedErroredCount = 0,
  unresolvedCountIsExact = true,
  warningCount,
  mappingBlockingCount,
  mappingAdvisoryCount,
  asOfWarningMessage,
  doubleCountWarningMessage,
  completeness,
}: ReviewActionsProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reprocessConfirmOpen, setReprocessConfirmOpen] = useState(false);
  // "More" overflow menu (#7441, artboard 1j): Reject and Delete moved out of
  // the primary row so Approve is the one prominent action and a destructive
  // choice takes two deliberate steps (open menu → pick → dialog).
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const moreOpen = Boolean(moreAnchorEl);
  const closeMore = () => setMoreAnchorEl(null);

  // Names what the reviewer actually stands to lose.
  //
  // `excludedCount` is the REVIEWER'S divergence from the auto-exclude pass, not
  // the size of the exclusion set (!4711 review, [AEM-autoexclude-attribution]).
  // The set is the union of both, so a document with 37 junk rows and an
  // untouched reviewer used to read "discards 37 record exclusions".
  //
  // The paid-through date is deliberately NOT listed. Nothing resets a
  // reviewer-typed as-of on a reprocess, so claiming it is discarded was an
  // over-warning — the inverse of the failure AC2 names, and the one this PR
  // argued against at length while shipping it here
  // ([AEM-asof-not-discarded]). Keeping the date is also the behaviour you
  // want: it is the reviewer's assertion about the carrier's reporting period,
  // a property of the file rather than of one extraction run.
  const { pendingWork, message: reprocessMessage } = reprocessConfirmCopy(excludedCount);

  const mappingDisabled = !hasTransformation || isClientMissing;

  // isClientMissing is in here deliberately: the POST resolves to a 400
  // "Client ID is required" for a non-multi-client document without one, so
  // offering the control in that state offers a guaranteed failure
  // (!4711 review, [AEM-client-id-unresolved]). Mirrors mappingDisabled beside
  // it, which already gates on the same term.
  const reprocessDisabled = !hasTransformation || isClientMissing || isReprocessing || isApproving || isRejecting;
  const reprocessDisabledReason = !hasTransformation
    ? "Available once the document has a template and field mappings."
    : isClientMissing
      ? "Select a client first."
      : isReprocessing
        ? "A reprocess is already running for this document."
        : "";
  const hasErrors = errorCount > 0 || unresolvedErroredCount > 0;
  // Confirm-before-approve: one derivation shared with the sticky bar (see
  // approveStatus.buildApproveConfirm) so both Approve buttons gate alike.
  const { needsConfirm, message: warningMessage } = buildApproveConfirm({
    hasErrors,
    warningCount,
    asOfWarningMessage,
    doubleCountWarningMessage,
    completeness,
  });
  const mappingIssueCount = mappingBlockingCount + mappingAdvisoryCount;
  const mappingBadgeColor = mappingBlockingCount > 0 ? "error" : "warning";

  // Why Approve is disabled, in words (#7431 AC2). The status line renders
  // under the button for EVERY disabling cause — before #7431 only extraction
  // errors got a tooltip, and a missing client, blocking mapping issues, or a
  // future as-of disabled the button silently.
  const approveStatus = deriveApproveStatus({
    readOnly,
    partnerReadOnly,
    isClientMissing,
    nothingToLoad,
    isChecking,
    errorCount,
    unresolvedErroredCount,
    mappingBlockingCount,
    isAsOfInFuture,
  });

  // Base readiness + block when extraction errors exist. approveStatus.blocked
  // covers the named causes; isReadyForApproval keeps any caller-side gate
  // (e.g. multi-client "nothing to load") that has no line of its own yet.
  const approveDisabled = isApproving || isRejecting || !isReadyForApproval || hasErrors || approveStatus.blocked;

  const handleApproveClick = () => {
    if (needsConfirm) {
      setConfirmOpen(true);
    } else {
      onApprove();
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    onApprove();
  };

  // Tooltip for the disabled approve button when errors exist.
  //
  // DOES NOT SAY "LOAD MORE RECORDS" (!4682 review). It used to, for errors
  // beyond the loaded page — and since #6853 the banner three inches above
  // says "Loading more records will not help", because it does not: the
  // errored read already has them, and paging to a record changes nothing
  // about why it errored. Two adjacent instructions that contradict each other
  // is worse than either alone.
  //
  // The resolutions are the banner's: fix the mapping, or exclude the rows.
  // Named here too rather than deferring to it, because a reviewer hovering a
  // disabled button is asking this control what to do.
  const unresolvedPart =
    unresolvedErroredCount > 0
      ? ` ${unresolvedErroredCount.toLocaleString()} record${unresolvedErroredCount !== 1 ? "s" : ""} in this document ${unresolvedErroredCount !== 1 ? "have" : "has"} errors${
          unresolvedCountIsExact
            ? ""
            : " (at least — this document has more errored records than could be checked at once)"
        }. Adjust the field mapping if a column is mis-mapped, or exclude the rows.`
      : "";
  const approveTooltip = hasErrors
    ? (errorCount > 0
        ? `${errorCount} error${errorCount !== 1 ? "s" : ""} must be resolved before approving. Try adjusting the field mapping to fix extraction issues.`
        : "Errors must be resolved before approving.") + unresolvedPart
    : "";

  const moreOnly = variant === "more-only";

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
      {!moreOnly && (
        <Tooltip
          title={isClientMissing && hasTransformation ? "Select a client first to edit field mappings" : ""}
          arrow
          disableHoverListener={!isClientMissing || !hasTransformation}
        >
          <span>
            <Badge
              badgeContent={mappingIssueCount}
              color={mappingBadgeColor}
              invisible={mappingIssueCount === 0}
              max={99}
            >
              <MUIButton
                size={UIComponentSize.Small}
                variant={ButtonStyleType.Text}
                onClick={onReviewMapping}
                disabled={mappingDisabled}
                startIcon={<PreviewIcon />}
                sx={{ whiteSpace: "nowrap" }}
              >
                Field Mapping
              </MUIButton>
            </Badge>
          </span>
        </Tooltip>
      )}

      {/* Hidden for a read-only partner too (ADR-035): the server would reject
          the write, and ClientGate's reject link and Reprocess already hide. */}
      {!readOnly && !partnerReadOnly && (
        <MUIButton
          size={UIComponentSize.Small}
          variant={ButtonStyleType.Outlined}
          onClick={(e) => setMoreAnchorEl(e.currentTarget)}
          disabled={isRejecting || isApproving}
          endIcon={<ExpandMoreIcon />}
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={moreOpen ? "true" : undefined}
          sx={{ whiteSpace: "nowrap" }}
        >
          {isRejecting ? "Rejecting..." : "More"}
        </MUIButton>
      )}
      <Menu
        anchorEl={moreAnchorEl}
        open={moreOpen}
        onClose={closeMore}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        {onReprocess && (
          // Reprocess moved under More (#7431, artboard 1a) so the header's
          // primary row is Field Mapping + Approve. The disabled reason that
          // used to be a tooltip (!4711) is the item's secondary line.
          <MenuItem
            onClick={() => {
              closeMore();
              setReprocessConfirmOpen(true);
            }}
            disabled={reprocessDisabled}
            sx={{ alignItems: "flex-start", py: 1 }}
          >
            <ListItemText
              primary={isReprocessing ? "Reprocessing…" : "Reprocess…"}
              secondary={reprocessDisabledReason || "Re-runs the file from source; keeps field mappings"}
              primaryTypographyProps={{ sx: { fontWeight: 600 } }}
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            closeMore();
            onReject();
          }}
          sx={{ alignItems: "flex-start", py: 1 }}
        >
          <ListItemText
            primary="Reject file…"
            secondary="Nothing loads; the uploader sees why"
            primaryTypographyProps={{ sx: { fontWeight: 600, color: "error.main" } }}
          />
        </MenuItem>
        {/* Rendered disabled (not hidden) when the caller lacks
            documents:cascade-delete, so a reviewer learns the action exists
            and who to ask, instead of hunting for it (#7441 AC1). A Tooltip
            on a disabled MenuItem would need a wrapper span; the secondary
            line carries the reason instead. */}
        <MenuItem
          onClick={() => {
            closeMore();
            onDeleteDocument?.();
          }}
          disabled={!onDeleteDocument}
          sx={{ alignItems: "flex-start", py: 1 }}
        >
          <ListItemText
            primary="Delete document…"
            secondary={onDeleteDocument ? "Removes the file and what it created" : "Admins only"}
            primaryTypographyProps={{ sx: { fontWeight: 600 } }}
          />
        </MenuItem>
      </Menu>

      {!moreOnly && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
          <Tooltip title={approveTooltip} arrow disableHoverListener={!hasErrors}>
            {/* span wrapper so Tooltip works on a disabled button */}
            <span>
              <MUIButton
                size={UIComponentSize.Small}
                variant={ButtonStyleType.Contained}
                onClick={handleApproveClick}
                disabled={approveDisabled}
                startIcon={<CheckIcon />}
                aria-describedby={approveStatus.why ? APPROVE_STATUS_ID : undefined}
                sx={{ whiteSpace: "nowrap" }}
              >
                {isApproving ? "Approving..." : "Approve and load"}
              </MUIButton>
            </span>
          </Tooltip>
          {approveStatus.why && (
            <Typography
              id={APPROVE_STATUS_ID}
              variant="caption"
              sx={{
                whiteSpace: "nowrap",
                color:
                  approveStatus.kind === "attention"
                    ? "warning.dark"
                    : approveStatus.kind === "no-client"
                      ? "warning.dark"
                      : "text.secondary",
              }}
            >
              {approveStatus.why}
            </Typography>
          )}
        </Box>
      )}

      <ConfirmationDialog
        open={reprocessConfirmOpen}
        title="Reprocess this document?"
        message={reprocessMessage}
        confirmLabel={ConfirmLabelType.Continue}
        cancelLabel={CancelLabelType.Cancel}
        confirmColor={pendingWork ? ColorType.Warning : ColorType.Primary}
        onConfirm={() => {
          setReprocessConfirmOpen(false);
          onReprocess?.();
        }}
        onCancel={() => setReprocessConfirmOpen(false)}
      />

      {!moreOnly && (
        <ConfirmationDialog
          open={confirmOpen}
          title="Approve with Warnings?"
          message={warningMessage}
          confirmLabel={ConfirmLabelType.Continue}
          cancelLabel={CancelLabelType.Cancel}
          confirmColor={ColorType.Warning}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </Box>
  );
};
